/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드 공통 클래스를 정의한다.
 *
 * @file /src/components/grid/Grid.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import * as parent from '@models/table/Table';
import { ListStores, TreeStores } from '@models/store/Store';
import { Header } from '@models/table/Header';
import { Pagination } from '@components/pagination/Pagination';
import { Html } from '@utils/Html';
import { Dom } from '@utils/Dom';
import './Grid.css';

export interface Listeners<T extends parent.Table<any, any>> extends parent.Listeners<T> {}

export interface Properties<
    S extends ListStores | TreeStores,
    T extends parent.Table<any, any>,
> extends parent.Properties<S, T> {
    /**
     * 컬럼 크기조절 여부
     */
    columnResizable?: boolean;

    /**
     * 페이지네이션
     */
    pagination?: Pagination | Pagination['properties'];

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Grid<S extends ListStores | TreeStores, T extends parent.Table<any, any>> extends parent.Table<S, T> {
    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'grid';

    /**
     * 모드
     */
    mode: string;

    /**
     * 페이지네이션
     */
    pagination: Pagination = null;

    /**
     * 그리드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<S, T> = null) {
        super(properties);

        this.properties.pagination ??= null;
        if (this.properties.pagination !== null) {
            if (this.properties.pagination instanceof Pagination) {
                this.pagination = this.properties.pagination;
            } else {
                this.pagination = new Pagination({ ...this.properties.pagination });
            }
            this.pagination.setParent(this);
            this.pagination.setStore(this.getStore());
            this.bottombars.push(this.pagination);
        }

        this.header = new Header({
            table: this as any,
            columnResizable: this.properties.columnResizable !== false,
        });
    }

    /**
     * 그리드의 모든 데이터를 가져온다.
     *
     * @param fields - 데이터를 가져올 필드 (기본값 : * - 그리드에 정의된 모든 컬럼의 필드)
     */
    getData(fields: string[] = ['*']): { [key: string]: any }[] {
        const expanded = fields.flatMap((f) =>
            f === '*'
                ? [
                      ...new Set([
                          ...this.getColumns()
                              .map((c) => c.dataIndex)
                              .filter((d) => d !== null),
                          ...Object.keys(this.getStore().fields),
                      ]),
                  ]
                : [f]
        );

        return this.getStore()
            .getRecords()
            .map((record) => Object.fromEntries(expanded.map((f) => [f, record.get(f)])));
    }

    /**
     * 순차정렬이 적용되어 있는 테이블에서 선택된 열의 위치를 이동한다.
     *
     * @param direction - 이동할 방향
     */
    async moveSelections(direction: 'up' | 'down'): Promise<void> {
        if (this.getSelection().getSelections().length == 0) {
            return;
        }

        const sorters = this.getStore().getSorters();
        if (sorters === null || Object.keys(sorters).length !== 1) {
            return;
        }
        const sortField = Object.keys(sorters).pop();

        // @todo 필터가 걸려있다면 경고

        /**
         * 현재 정렬필드의 타입이 숫자가 아닌 경우 정렬을 중단한다.
         */
        if (this.getStore().fields[sortField]?.type !== 'int') {
            return;
        }

        /**
         * 선택된 행을 가져온다.
         */
        const $rows = Html.all('div[data-role=row].select', this.getBody().$getBody()).getList();
        if (direction == 'down') {
            $rows.reverse();
        }

        let isUpdated: boolean = false;
        for (const $row of $rows) {
            /**
             * 선택된 행이 이동할 행을 가져온다.
             */
            let $movable: Dom;
            let $rowWrapper: Dom = null;
            let $movableWrapper: Dom = null;

            if (this.isTree() === true) {
                $rowWrapper = $row.getParent();
                const wrapperSelector = 'div[data-model=table]:is([data-role=list],[data-role=tree])';
                if (direction == 'up') {
                    $movableWrapper = $rowWrapper?.prev(wrapperSelector) ?? null;
                    $movable =
                        $movableWrapper !== null
                            ? Html.get('div[data-model=table][data-role=row]', $movableWrapper)
                            : null;
                } else {
                    $movableWrapper = $rowWrapper?.next(wrapperSelector) ?? null;
                    $movable =
                        $movableWrapper !== null
                            ? Html.get('div[data-model=table][data-role=row]', $movableWrapper)
                            : null;
                }
            } else if (direction == 'up') {
                $movable = $row.prev('div[data-model=table][data-role=row]');
            } else {
                $movable = $row.next('div[data-model=table][data-role=row]');
            }

            /**
             * 이동해야할 행이 없다면 중단한다.
             */
            if ($movable === null) {
                continue;
            }

            /**
             * 이동할 행이 선택되어 있다면 중단한다.
             */
            if ($movable.hasClass('select') == true) {
                continue;
            }

            const row = this.getStore().get($row.getData('record-id'));
            const move = this.getStore().get($movable.getData('record-id'));
            if (row === null || move === null) {
                continue;
            }

            /**
             * 정렬필드의 값을 치환한다.
             */
            const origin = row.get(sortField);

            await row.set(sortField, move.get(sortField), false);
            await move.set(sortField, origin, false);

            /**
             * DOM 위치를 치환한다.
             */
            if (this.isTree() === true) {
                if (direction == 'up') {
                    $movableWrapper.getParent().insertBefore($rowWrapper, $movableWrapper);
                } else {
                    $movableWrapper.getParent().insertBefore($movableWrapper, $rowWrapper);
                }
            } else if (direction == 'up') {
                $movable.getParent().insertBefore($row, $movable);
            } else {
                $movable.getParent().insertBefore($movable, $row);
            }

            /**
             * 데이터스토어에서 위치를 변경한다.
             */
            await this.getStore().swap(row.getId(), move.getId(), false);

            isUpdated = true;
        }

        if (isUpdated == true) {
            await this.getStore().reindex(sortField, false);
            await this.fireEvent('update', [this]);
        }
    }

    /**
     * 데이터 형태가 트리인지 확인한다.
     *
     * @returns 트리여부
     */
    isTree(): boolean {
        return this.mode == 'tree';
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();
        this.$getComponent().setAttr('data-mode', this.mode);
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await super.remove(removeDom);
    }
}
